import { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { IconArrowLeftOutline, Page, PageContent, Spinner, Text } from '@zillow/constellation';
import { Button, Heading } from '@zillow/constellation-10';

import {
  useRentalManagerUserPersonaLinkWebApiHandler_getLink,
  useRentalManagerUserPropertyPersonaLinkWebApiHandler_getLink,
} from '@zg-rentals/rental-manager-env';
import { getGlobalMonitor } from '@zg-rentals/monitor-browser';

import { useSelector } from '../../selectors/useSelector';
import { StyledFlex } from './StyledComponents';

import type { DatarouterError } from '@zg-rentals/rental-manager-env';

const SecurityInquiryPage = () => {
  const location = useLocation<{ listingAlias?: string }>();
  const history = useHistory();
  const monitor = getGlobalMonitor();

  const listingAlias = location?.state?.listingAlias ?? '';
  const isUserBlocked = useSelector((state) => state.user.isUserBlocked);
  const isDirectLoad = !isUserBlocked && !listingAlias;
  const pageHeading = isUserBlocked ? 'Redirecting...' : 'Opening in new tab...';

  const { data: userLink, error: userLinkError } = useRentalManagerUserPersonaLinkWebApiHandler_getLink({
    enabled: isUserBlocked,
  });

  const { data: listingLink, error: listingLinkError } = useRentalManagerUserPropertyPersonaLinkWebApiHandler_getLink(
    { listingAlias },
    {
      enabled: Boolean(listingAlias),
    },
  );

  useEffect(() => {
    if (isDirectLoad) {
      monitor.count({
        name: 'SecurityInquiryPage direct load',
      });
    }
  }, [isDirectLoad]);

  const redirectUrl = userLink?.inquiryLink || listingLink?.inquiryLink;
  const isError = userLinkError || listingLinkError || isDirectLoad;
  const errorMessage =
    (userLinkError as DatarouterError)?.message ||
    (listingLinkError as DatarouterError)?.message ||
    'Something went wrong';

  useEffect(() => {
    if (redirectUrl) {
      handleRedirect();
    }
  }, [redirectUrl, history]);

  const handleRedirect = () => {
    if (isUserBlocked) {
      window.location.replace(redirectUrl as string);
    } else {
      window.open(redirectUrl, '_blank');
      history.goBack();
    }
  };

  return (
    <Page data-testid="SecurityInquiryPage">
      <PageContent>
        <StyledFlex display="flex" alignItems="center" justifyContent="center" flexDirection="column">
          {!isError && <Spinner size="lg" marginBottom="sm" />}
          <Heading
            level={1}
            textStyle="heading-sm"
            css={{ textAlign: 'center' }}
            data-testid="SecurityInquiryPage-Heading"
          >
            {isError ? errorMessage : pageHeading}
          </Heading>
          <>
            <Text textAlign="center" marginY="sm" data-testid="SecurityInquiryPage-Subheading">
              {isError ? 'Please try again later' : 'Do not refresh the page'}
            </Text>
            {redirectUrl && (
              <Button tone="brand" emphasis="filled" onClick={handleRedirect}>
                Click to manually open
              </Button>
            )}
            {isError && (
              <Button
                tone="critical"
                emphasis="filled"
                icon={<IconArrowLeftOutline />}
                iconPosition="start"
                onClick={() => (isUserBlocked ? window.close() : history.goBack())}
                data-testid="SecurityInquiryPage-BackButton"
              >
                Return
              </Button>
            )}
          </>
        </StyledFlex>
      </PageContent>
    </Page>
  );
};

export default SecurityInquiryPage;
